import { FaBolt, FaSyncAlt } from 'react-icons/fa';
import Button from '@/components/UI/Button';
import FormSelect from '@/components/UI/FormSelect';
import type { EntityTypeUnion } from '@einhaus/project314-types';

const entityTypeOptions: { value: '' | EntityTypeUnion['entityType']; label: string }[] = [
    { value: '', label: 'All entity types' },
    { value: 'Asset', label: 'Asset' },
    { value: 'Cryptocurrency', label: 'Cryptocurrency' },
    { value: 'AssetIndex', label: 'Asset Index' }
];

interface BacktestOperationsToolbarProps {
    activeQueueJobCount: number;
    canManage: boolean;
    selectedEntityType: '' | EntityTypeUnion['entityType'];
    lastUpdatedLabel: string | null;
    isRefreshing: boolean;
    isPopulating: boolean;
    onEntityTypeChange: (entityType: '' | EntityTypeUnion['entityType']) => void;
    onRefresh: () => void;
    onPopulateMissing: () => void;
}

/**
 * Scope and actions for a strategy's backtest operations. There is deliberately no strategy picker:
 * the surrounding page already establishes which strategy this is.
 */
const BacktestOperationsToolbar = ({
    activeQueueJobCount,
    canManage,
    selectedEntityType,
    lastUpdatedLabel,
    isRefreshing,
    isPopulating,
    onEntityTypeChange,
    onRefresh,
    onPopulateMissing
}: BacktestOperationsToolbarProps) => {
    const isLivePolling = activeQueueJobCount > 0;

    return (
        <div className="rounded-lg border border-zinc-800 bg-[#161616] p-4">
            <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[200px]">
                    <FormSelect
                        noMargin
                        id="entityTypeSelect"
                        label="Entity type"
                        options={entityTypeOptions}
                        value={selectedEntityType}
                        onChange={(event) => onEntityTypeChange(event.target.value as '' | EntityTypeUnion['entityType'])}
                    />
                </div>
                <div className="pb-2 text-sm text-zinc-400">
                    Coverage, queue status, and performance are scoped to the current strategy version.
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-3">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="inline-flex items-center gap-2 text-xs text-zinc-400">
                        <span
                            aria-hidden="true"
                            className={`h-2 w-2 rounded-full ${
                                isLivePolling ? 'animate-pulse bg-emerald-400 motion-reduce:animate-none' : 'bg-zinc-600'
                            }`}
                        />
                        <span>
                            {isLivePolling
                                ? `Live · monitoring ${activeQueueJobCount} active backtest job${activeQueueJobCount === 1 ? '' : 's'}`
                                : 'No active backtests · checking periodically'}
                        </span>
                    </div>
                    {lastUpdatedLabel && <span className="text-xs text-zinc-500">Updated {lastUpdatedLabel}</span>}
                    <span className="text-xs text-zinc-600">Previous versions are excluded.</span>
                </div>
                <div className="flex gap-2">
                    <Button
                        disabled={isRefreshing}
                        icon={<FaSyncAlt aria-hidden="true" className={isRefreshing ? 'animate-spin' : ''} />}
                        size="sm"
                        variant="secondary"
                        onClick={onRefresh}
                    >
                        Refresh
                    </Button>
                    <Button
                        disabled={!canManage || isPopulating}
                        icon={<FaBolt aria-hidden="true" />}
                        isLoading={isPopulating}
                        size="sm"
                        variant="primary"
                        onClick={onPopulateMissing}
                    >
                        Populate missing
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BacktestOperationsToolbar;
